<script setup lang="ts">
  import type { PropType } from 'vue'

  import { computed, ref, watch } from 'vue'

  import { useRoute, useRouter } from 'nuxt/app'
  import { useI18n } from 'vue-i18n'

  import { ChevronLeftIcon, XCloseIcon } from '@gohighlevel/ghl-icons/24/outline'

  import { createElementId } from '@common/core-layer/utils/core.utils'
  import { useCPNavigation } from '@common/navigation-layer/composables/useNavigationContext'

  import CPInput from '@ui/components/CPInput.vue'

  import { useCoursesSearchContext } from '../composables/useCoursesSearchContext'

  const props = defineProps({
    ariaLabel: {
      type: String,
      default: '',
    },
    ariaControls: {
      type: String,
      default: undefined,
    },
    ariaExpanded: {
      type: Boolean,
      default: false,
    },
    behavior: {
      type: String as PropType<'route' | 'local'>,
      default: 'route',
    },
    compact: {
      type: Boolean,
      default: false,
    },
    modelValue: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: '',
    },
  })

  const emit = defineEmits<{
    focus: []
    submit: [query: string]
    'update:modelValue': [value: string]
  }>()

  const route = useRoute()
  const router = useRouter()
  const { CPNavigateTo } = useCPNavigation()
  const { initialQuery, onInputChange, onSubmit } = useCoursesSearchContext(route, router)
  const { t } = useI18n()

  const inputRef = ref<InstanceType<typeof CPInput> | null>(null)
  const searchInput = ref('')

  const isLocalBehavior = computed(() => props.behavior === 'local')
  const resolvedInputValue = computed(() => (isLocalBehavior.value ? props.modelValue : initialQuery.value))
  const nativeInputProps = computed(() => ({
    'aria-label': props.ariaLabel || t('courses.search.searchBar.placeholder'),
    ...(props.ariaControls
      ? {
          'aria-controls': props.ariaControls,
          'aria-expanded': props.ariaExpanded,
          'aria-haspopup': 'menu',
          role: 'combobox',
        }
      : {}),
  }))

  const handleInput = (value: string) => {
    searchInput.value = value
    if (isLocalBehavior.value) {
      emit('update:modelValue', value)
    } else {
      onInputChange?.(value)
    }
  }

  const handleFocus = () => {
    emit('focus')
  }

  const handleSubmit = async () => {
    const query = searchInput.value.trim()
    emit('submit', query)

    if (!isLocalBehavior.value) {
      await onSubmit(query)
    }
  }

  const handleBack = async () => {
    // If we're in filtered view, go back to normal search results
    if (route.query.filter) {
      const { filter, ...restQuery } = route.query
      void filter // Unused but needed for destructuring
      router.replace({
        query: restQuery,
      })
      return
    }

    // Otherwise, navigate back normally
    if (window.history.length > 1) {
      window.history.back()
    } else {
      await CPNavigateTo({ name: 'courses' }, true)
    }
  }

  const handleClear = () => {
    handleInput('')
    inputRef.value?.focus()
  }

  watch(
    resolvedInputValue,
    value => {
      searchInput.value = value
    },
    { immediate: true }
  )
</script>
<template>
  <div class="w-full">
    <CPInput
      :id="createElementId('search-bar', 'input', 'search')"
      ref="inputRef"
      class="w-full"
      :model-value="searchInput"
      type="text"
      :placeholder="props.placeholder || t('courses.search.searchBar.placeholder')"
      :size="props.compact ? 'sm' : 'md'"
      :input-props="nativeInputProps"
      @focus="handleFocus"
      @keydown.enter.prevent="handleSubmit"
      @update:model-value="handleInput"
    >
      <template v-if="!props.compact && !isLocalBehavior" #prefix>
        <ChevronLeftIcon :id="createElementId('search-bar', 'icon', 'back')" class="text-body h-5 w-5 cursor-pointer" @click="handleBack" />
      </template>
      <template v-if="searchInput" #suffix>
        <XCloseIcon
          :id="createElementId('search-bar', 'icon', 'clear')"
          class="text-body h-4 w-4 cursor-pointer"
          @click.stop="handleClear"
        />
      </template>
    </CPInput>
  </div>
</template>
